import * as React from "react";
import {useEffect, useImperativeHandle, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import { toast } from "sonner";
import {CaseFile} from "../../models/CaseFile";
import {MigrationStatus} from "../../models/MigrationStatus";
import CasesRepository from "../../repositories/CasesRepository";
import MigrationStatusChip from "../common/MigrationStatusChip";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import {FileType, FileTypeMap} from "../../models/FileType";
import {CaseFileFilters} from "../../models/CaseFileFilters";
import JobsRepository from "../../repositories/JobsRepository";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../common/ActionConfirmationModal";
import TextHighlighter from "../common/TextHighlighter";
import {useTableHandlers} from "../common/Table/Hooks/UseTableHandlers";
import {useTableFileFilters} from "../common/Table/Hooks/UseTableFileFilters";
import {QUERY_KEYS} from "../../constants/QueryKeys";
import {
  fileFiltersBoxClassName,
  filterComboboxClassName,
  tableWrapperClassName,
} from "../common/Table/TableStyles";
import {NumberInput} from "../common/Table/NumberInput";
import {FileStatusFilter} from "../common/Table/File/FileStatusFilter";
import {FileExtensionsFilter} from "../common/Table/File/FileExtensionsFilter";
import {SearchInput} from "../common/Table/SearchInput";
import TablePagination from "../common/Table/TablePagination";
import TableEmptyState from "../common/TableEmptyState";
import {FilterCombobox} from "../common/FilterCombobox";
import {QUERY_CONFIG} from "../../constants/QueryConfig";
import {ActiveJobsExplorer} from "../../models/Jobs/ActiveJobsExplorer";
import {FileFailureTypeFilter} from "../common/Table/File/FileFailureTypeFilter";
import {FileFailureTypeChip} from "../common/Table/File/FileFailureTypeChip";
import {FileConflictFilter} from "../common/Table/File/FileConflictFilter";
import {FileConflictChip} from "../common/Table/File/FileConflictChip";
import { cn } from "@/lib/utils";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Trash2, RotateCcw } from "lucide-react";

interface CaseFilesTableProps {
  organizationId: string;
  activeJobsExplorer: ActiveJobsExplorer;
  newCaseFilesForDeletion: CaseFile[];
  setNewCasesFilesForDeletion: React.Dispatch<React.SetStateAction<CaseFile[]>>;
  caseId?: string;
  disabled?: boolean;
  toolbar?: React.ReactNode;
}

export interface CaseFilesTableRef {
  exportCurrentViewToCsv: (reason?: string) => void;
}

export function CaseFilesTable({
  ref,
  organizationId,
  activeJobsExplorer,
  newCaseFilesForDeletion,
  setNewCasesFilesForDeletion,
  caseId,
  disabled,
  toolbar
}: CaseFilesTableProps & { ref?: React.Ref<CaseFilesTableRef> }) {
  const casesRepository = new CasesRepository();
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();
  const [caseFiles, setCaseFiles] = useState<CaseFile[]>([]);

  const [extensionFilter, setExtensionFilter] = React.useState<string | null>(null);
  const [nestedFolder1Filter, setNestedFolder1Filter] = React.useState<string | null>(null);
  const [nestedFolder2Filter, setNestedFolder2Filter] = React.useState<string | null>(null);
  const [fileTypeFilter, setFileTypeFilter] = React.useState<string | null>(FileType.All);
  const [conflictReasonFilter, setConflictReasonFilter] = React.useState<string | null>(null);
  const [conflictStatusFilter, setConflictStatusFilter] = React.useState<string | null>(null);
  const [caseFilesFilters, setCaseFilesFilters] = useState<CaseFileFilters>();

  const {
    order,
    sort,
    page,
    rowsPerPage,
    rows,
    setRows,
    setPage,
    changeSort,
    changeOrder,
    handleRowsPerPageChange
  } = useTableHandlers({
    order: "asc",
    sort: "status",
    page: 0,
    rowsPerPage: 100
  });

  const {
    statusFilter,
    failureType,
    filePathFilter,
    destinationPathFilter,
    minSizeFilter,
    maxSizeFilter,
    extensions,
    extensionsSearch,
    setStatusFilter,
    setFailureType,
    setExtensions,
    setExtensionsSearch,
    handleDestinationPathSearch,
    handleFilePathSearch,
    handleMaxSizeFilter,
    handleMinSizeFilter
  } = useTableFileFilters();

  const caseFilesQuery = useQuery({ queryKey: [
      QUERY_KEYS.CASE_FILES,
      organizationId,
      caseId,
      page,
      rowsPerPage,
      statusFilter,
      failureType,
      sort,
      order,
      extensionsSearch,
      extensionFilter,
      nestedFolder1Filter,
      nestedFolder2Filter,
      filePathFilter,
      destinationPathFilter,
      minSizeFilter,
      maxSizeFilter,
      fileTypeFilter,
      conflictReasonFilter,
      conflictStatusFilter,
    ], queryFn:
    async () => await casesRepository.getCaseFilesForCase(caseId, {
      descending: order === "desc",
      orderBy: sort,
      nestedFolder1: nestedFolder1Filter,
      nestedFolder2: nestedFolder2Filter,
      minFileSize: minSizeFilter,
      maxFileSize: maxSizeFilter,
      filePath: filePathFilter,
      destinationPath: destinationPathFilter,
      pageNumber: page + 1,
      pageSize: rowsPerPage,
      failureType: failureType,
      status: statusFilter
        ? [statusFilter]
        : null,
      type: fileTypeFilter
        ? FileTypeMap[fileTypeFilter]
        : null,
      extension:
        extensionsSearch || extensionFilter
          ? extensionFilter
            ? [...extensionsSearch, extensionFilter]
            : extensionsSearch
          : null,
      conflictReason: conflictReasonFilter ? [conflictReasonFilter] : null,
      conflictStatus: conflictStatusFilter ? [conflictStatusFilter] : null,
    }), staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
      refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });

  useEffect(() => {
    if (caseFilesQuery.data?.data) {
      setCaseFiles(caseFilesQuery.data?.data);
      setRows(caseFilesQuery.data.paging?.totalCount ?? 0);
    }
  }, [caseFilesQuery]);

  const caseFilesFiltersQuery = useQuery({ queryKey: [QUERY_KEYS.CASE_FILES_FILTERS, organizationId, caseId], queryFn:
    async () => await casesRepository.getCaseFilesFiltersForCase(caseId!), enabled: !!caseId,
      staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
      refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });

  useEffect(() => {
    const filtersData = caseFilesFiltersQuery.data?.data;
    if (filtersData)
      setCaseFilesFilters(filtersData);
  }, [caseFilesFiltersQuery]);

  const exportCurrentViewToCsv = async (reason?: string) => {
    const options = {
      descending: order === "desc",
      orderBy: sort,
      nestedFolder1: nestedFolder1Filter,
      nestedFolder2: nestedFolder2Filter,
      minFileSize: minSizeFilter,
      maxFileSize: maxSizeFilter,
      filePath: filePathFilter,
      destinationPath: destinationPathFilter,
      failureType: failureType,
      status: statusFilter
        ? [statusFilter]
        : null,
      type: fileTypeFilter
        ? FileTypeMap[fileTypeFilter]
        : null,
      extension:
        extensionsSearch || extensionFilter
          ? extensionFilter
            ? [...extensionsSearch, extensionFilter]
            : extensionsSearch
          : null,
      conflictReason: conflictReasonFilter ? [conflictReasonFilter] : null,
      conflictStatus: conflictStatusFilter ? [conflictStatusFilter] : null,
    };
    try {
      await jobsRepository.startCsvGenerationFromCaseFiles(organizationId, options, reason, caseId);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPORT_CASE_FILES, caseId] });
      toast.success("Case files export started");
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to start case files export", { duration: 8000 });
    }
  };

  useImperativeHandle(ref, () => ({
    exportCurrentViewToCsv,
  }));

  const handleMarkNotFoundFileForRetry = async (caseId: string, fileId: string, reason: string) => {
    await casesRepository.markNotFoundFileForRetry(caseId, fileId, reason);
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASE_FILES] });
  }

  const handleAddCaseFileForDeletion = (caseFileEntity: CaseFile) => {
    if (!disabledRows.has(caseFileEntity.id))
      setNewCasesFilesForDeletion((prevRows) => [...prevRows, caseFileEntity,]);
  };

  const getDisabledRows = () => {
    switch (activeJobsExplorer) {
      case ActiveJobsExplorer.Delete:
        return new Set(newCaseFilesForDeletion.map((c) => c.id));
    }
    return new Set<string>();
  };

  const getActiveButtonForExplorer = (
    caseFileEntity: CaseFile,
    isDisabled: boolean
  ) => {
    switch (activeJobsExplorer) {
      case ActiveJobsExplorer.Delete:
        return (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Delete"
            disabled={
              isDisabled
              || disabled
            }
            className="text-destructive"
            onClick={() => handleAddCaseFileForDeletion(caseFileEntity)}
          >
            <Trash2 className="size-4" />
          </Button>
        );

      default:
        <></>;
    }
  };

  const disabledRows = getDisabledRows();

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      {toolbar}
      <div className={fileFiltersBoxClassName}>
        <FileExtensionsFilter
          extensions={extensions}
          onExtensionsChange={setExtensions}
          onSearch={() => setExtensionsSearch(extensions)}
        />
        <NumberInput
          label="Files min size"
          placeholder="Min size"
          onBlur={(e) => handleMinSizeFilter(e.target.value)}
        />
        <NumberInput
          label="Files max size"
          placeholder="Max size"
          onBlur={(e) => handleMaxSizeFilter(e.target.value)}
        />
        <FileStatusFilter
          value={statusFilter}
          onChange={(newValue) => {
            setPage(0);
            setStatusFilter(newValue);
          }}
        />
        <FileFailureTypeFilter
          value={failureType}
          onChange={(newValue) => {
            setPage(0);
            setFailureType(newValue);
          }}
        />
        <FileConflictFilter
          reason={conflictReasonFilter}
          status={conflictStatusFilter}
          onReasonChange={(newValue) => {
            setPage(0);
            setConflictReasonFilter(newValue);
          }}
          onStatusChange={(newValue) => {
            setPage(0);
            setConflictStatusFilter(newValue);
          }}
        />
      </div>

      {caseId && (
        <div className={fileFiltersBoxClassName}>
          <div className={filterComboboxClassName}>
            <Label className="text-xs font-semibold text-muted-foreground">Extension</Label>
            <FilterCombobox
              value={extensionFilter}
              onChange={(newValue) => {
                setPage(0);
                setExtensionFilter(newValue);
              }}
              placeholder="Extension"
              searchable
              options={(caseFilesFilters?.extensions ?? []).map((ext) => ({
                value: ext,
                label: ext,
              }))}
            />
          </div>

          <div className={filterComboboxClassName}>
            <Label className="text-xs font-semibold text-muted-foreground">Document type</Label>
            <FilterCombobox
              value={nestedFolder1Filter}
              onChange={(newValue) => {
                setPage(0);
                setNestedFolder1Filter(newValue);
              }}
              placeholder="Document type"
              searchable
              options={(caseFilesFilters?.nestedFolders1 ?? []).map((ext) => ({
                value: ext,
                label: ext,
              }))}
            />
          </div>

          <div className={filterComboboxClassName}>
            <Label className="text-xs font-semibold text-muted-foreground">Document category</Label>
            <FilterCombobox
              value={nestedFolder2Filter}
              onChange={(newValue) => {
                setPage(0);
                setNestedFolder2Filter(newValue);
              }}
              placeholder="Document category"
              searchable
              options={(caseFilesFilters?.nestedFolders2 ?? []).map((ext) => ({
                value: ext,
                label: ext,
              }))}
            />
          </div>

          <div className={filterComboboxClassName}>
            <Label className="text-xs font-semibold text-muted-foreground">File type</Label>
            <FilterCombobox
              value={fileTypeFilter}
              onChange={(newValue) => {
                setPage(0);
                setFileTypeFilter(newValue);
              }}
              placeholder="File type"
              options={Object.values(FileType).map((type) => ({
                value: type as string,
                label: type as string,
              }))}
            />
          </div>
        </div>
      )}

      <div className={fileFiltersBoxClassName}>
        <SearchInput
          label="Source path / API ID"
          placeholder="File path or source API ID"
          onBlur={(e) => handleFilePathSearch(e.target.value)}
        />
        <SearchInput
          label="Destination path"
          placeholder="SharePoint path"
          onBlur={(e) => handleDestinationPathSearch(e.target.value)}
        />
      </div>

      <div
        className={cn(
          tableWrapperClassName,
          (caseFilesQuery.isFetching || caseFilesQuery.isError) && "opacity-50 pointer-events-none"
        )}
      >
        <Table containerClassName="flex-1 min-h-0">
            <TableHeader>
              <TableRow>
                {caseId === undefined && (
                  <TableHead className="w-[12%]">
                    <SortableTableColumnHeader
                      label={"Case ID"}
                      sortProperty={"caseId"}
                      getSort={() => sort}
                      setSort={(newSort) => changeSort(newSort)}
                      getOrder={() => order}
                      setOrder={(newOrder) => changeOrder(newOrder)}
                    />
                  </TableHead>
                )}
                <TableHead className="w-[13%]">
                  <SortableTableColumnHeader
                    label={"Status"}
                    sortProperty={"status"}
                    getSort={() => sort}
                    setSort={(newSort) => changeSort(newSort)}
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>
                <TableHead className="w-[16%]">
                  <SortableTableColumnHeader
                    label={"Failure Reason"}
                    sortProperty={"failureType"}
                    getSort={() => sort}
                    setSort={(newSort) => changeSort(newSort)}
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>
                <TableHead className="w-[12%]">
                  Conflict
                </TableHead>
                <TableHead className="w-[21%]">
                  File Path / Source Api File ID
                </TableHead>
                <TableHead className="w-[21%]">
                  Destination Path
                </TableHead>
                <TableHead className="w-[9%]">
                  <SortableTableColumnHeader
                    label={"Size (MB)"}
                    sortProperty={"fileSize"}
                    getSort={() => sort}
                    setSort={(newSort) => changeSort(newSort)}
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>
                <TableHead className="w-[8%]">
                  Extension
                </TableHead>
                <TableHead className="w-[50px]" />
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {caseFiles.map((caseFile: CaseFile) => {
                const isDisabled = disabledRows.has(caseFile.id);
                return (
                  <TableRow key={caseFile.id}>
                    {caseId === undefined && (
                      <TableCell className="whitespace-normal break-all">
                        <a href={`/organization/${organizationId}/case/${caseFile.caseId}`} className="underline hover:no-underline">
                          {caseFile.caseId}
                        </a>
                      </TableCell>
                    )}
                    <TableCell>
                      <MigrationStatusChip status={caseFile.status} isFile />
                    </TableCell>
                    <TableCell>
                      <FileFailureTypeChip
                        failureType={caseFile.failureType}
                        failureReason={caseFile.failureReason}
                      />
                    </TableCell>
                    <TableCell>
                      <FileConflictChip
                        conflictReason={caseFile.conflictReason}
                        conflictStatus={caseFile.conflictStatus}
                      />
                    </TableCell>
                    <TableCell className="whitespace-normal break-all">
                      <TextHighlighter
                        text={caseFile.filePath}
                        searchTerm={filePathFilter}
                        level="body-sm"
                      />
                    </TableCell>
                    <TableCell className="whitespace-normal break-all">
                      <TextHighlighter
                        text={caseFile.destinationPath}
                        searchTerm={destinationPathFilter}
                        level="body-sm"
                      />
                    </TableCell>
                    <TableCell>
                      <Badge variant="neutral">{caseFile.fileSizeInMb.toFixed(2)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="primary">{caseFile.extension ?? "-"}</Badge>
                    </TableCell>
                    <TableCell>
                      {getActiveButtonForExplorer(caseFile, isDisabled)}
                    </TableCell>
                    <TableCell>
                      {caseFile.status === MigrationStatus.NotFound && (
                        <ActionConfirmationModal
                          actionConfig={ACTION_CONFIGS.MARK_NOT_FOUND_FILE_FOR_RETRY}
                          onConfirm={async (reason) =>
                            await handleMarkNotFoundFileForRetry(caseId === undefined ? caseFile.caseId : caseId, caseFile.id, reason)}
                          buttonProps={{
                            disabled: isDisabled || disabled,
                            size: "sm",
                            color: "primary"
                          }}
                        tooltip="Mark for retry"
                        >
                          <Button variant="ghost" size="icon-sm" aria-label="MarkForRetry" className="text-primary">
                                  <RotateCcw className="size-4" />
                                </Button>
                        </ActionConfirmationModal>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {caseFiles.length === 0 && !caseFilesQuery.isFetching && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={99}>
                    <TableEmptyState title="No case files found" />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        <TablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          rows={rows}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>
    </div>
  );
};
